import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import DataTable, {
  createColumnHelper,
  createFilter,
  sorting,
} from "veneer/DataTable";
import List from "veneer/List";
import ListItem from "veneer/ListItem";

import {
  deploymentsSelector,
  deploymentsStoresLoadedSelector,
} from "cortex/ui/src/fleet/redux-store/deployments/selectors";
import { Deployment, Target } from "cortex/ui/src/fleet/view/strategy/model";
import { useAppSelector } from "cortex/ui/src/hooks";
import { makeGetAllAsArray } from "cortex/ui/src/redux-store/selectors";
import { LoadingIndicator } from "cortex/ui/src/ui/loader";
import {
  DEFAULT_TIME_FORMAT_WITH_TZ,
  utcToLocalFormat,
} from "cortex/ui/src/util/time";
import { Container } from "cortex/ui/src/view/layout/Container";
import { VIEW } from "cortex/ui/src/view/views";

const getAllDeploymentsAsArray = makeGetAllAsArray<Deployment>();
const filter = createFilter<Deployment>("strategy", "targetName");
const column = createColumnHelper<Deployment>();

const columns = [
  column.accessor("strategy", {
    header: "Strategy",
    cell: (prop) => {
      const row = prop.row.original;
      const value = prop.getValue();
      return (
        <Link to={`/v2/strategies/${row.strategy_id}/${VIEW.EDIT}`}>
          {value}
        </Link>
      );
    },
    sortingFn: "alphanumeric",
  }),
  column.accessor("device_count", {
    header: "# of Devices",
    cell: (prop) => prop.getValue(),
    sortingFn: sorting.number((row) => row.device_count),
  }),
  column.accessor("targets", {
    header: "Targets",
    cell: (prop) => {
      return formatTargets(prop.getValue());
    },
    enableSorting: false,
  }),
  column.accessor("deploy_date", {
    header: "Deploy Date",
    cell: (prop) => formatDateTime(prop.getValue()),
    sortingFn: sorting.datetime((row) => row.deploy_date),
  }),
];

export default function DeployList() {
  const deploymentsById = useAppSelector(deploymentsSelector);
  const deployments = getAllDeploymentsAsArray({ byId: deploymentsById });
  const rows: Deployment[] = useMemo(
    () =>
      deployments.map((deployment: Deployment) => ({
        ...deployment,
        targetName: (deployment.targets ?? [])
          .map((target: Target) => target?.name)
          .filter(Boolean)
          .join(" "),
      })),
    [deployments],
  );
  const storesLoaded = useAppSelector(deploymentsStoresLoadedSelector);

  return (
    <Container title="Past Deployments">
      {storesLoaded ? (
        <DataTable
          columns={columns}
          filter={filter}
          initialPageSize={10}
          initialSort={{ id: "deploy_date", desc: true }}
          rows={rows}
        />
      ) : (
        <LoadingIndicator />
      )}
    </Container>
  );
}

const formatDateTime = (date: string | undefined): string => {
  if (!date) {
    return "N/A";
  }
  return utcToLocalFormat(date, DEFAULT_TIME_FORMAT_WITH_TZ);
};

const formatTargets = (targets: ReadonlyArray<Target>) => {
  const tags = targets.filter((target) => target.type == "t");
  const devices = targets.filter((target) => target.type == "d");
  if (tags.length === 0 && devices.length === 0) {
    return "N/A";
  }

  let list = [];
  for (let [index, tag] of Object.entries(tags)) {
    list.push(
      <ListItem disablePadding key={`t-${index}`}>
        <Link
          to={{
            pathname: "/devices",
            state: { tag: String(tag.id) },
          }}
        >
          {tag.name}
        </Link>
      </ListItem>,
    );
  }

  for (let [index, device] of Object.entries(devices)) {
    list.push(
      <ListItem disablePadding key={`d-${index}`}>
        <Link to={`/devices/${device.id}`}>{device.name}</Link>
      </ListItem>,
    );
  }

  return (
    <List dense disablePadding>
      {list}
    </List>
  );
};
